import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { formatPrice } from '../../../shared/utils/format-price.util';
import { DeliveryZoneChargeField } from '../../types/delivery-zone.type';
import { CreateDeliveryZoneRequestDTO, DeliveryZoneChargeInput } from '../../types/dtos/create-delivery-zone.dto';
import {
  CHARGES_ERROR,
  DEFAULT_CHARGE_THRESHOLDS,
  areChargesValid,
  computeCostsTTC,
} from '../../utils/delivery-zone-charges.util';
import {
  DELIVERY_ZONE_NAME_ERROR,
  DELIVERY_ZONE_NAME_MAX_LENGTH,
  isDeliveryZoneNameValid,
} from '../../utils/delivery-zone-form-rules.util';

const NAME_INPUT_ID = 'create-delivery-zone-name';

const DEFAULT_CHARGES: DeliveryZoneChargeInput[] = DEFAULT_CHARGE_THRESHOLDS.map((threshold) => ({
  threshold,
  costsHT: 0,
}));

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateDeliveryZoneRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  chargeRow: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateDeliveryZoneDialog({ open, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [name, setName] = useState('');
  const [charges, setCharges] = useState<DeliveryZoneChargeInput[]>(DEFAULT_CHARGES);

  useEffect(() => {
    if (!open) return;

    setName('');
    setCharges(DEFAULT_CHARGES);
  }, [open]);

  const isNameInvalid = !isDeliveryZoneNameValid(name);
  const areChargesInvalid = !areChargesValid(charges);
  const canSubmit = !isNameInvalid && !areChargesInvalid && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleChargeChange = (index: number, field: DeliveryZoneChargeField, value: string) =>
    setCharges((previous) =>
      previous.map((charge, chargeIndex) => (chargeIndex === index ? { ...charge, [field]: Number(value) } : charge))
    );

  const handleSubmit = () => {
    if (!canSubmit) return;

    onSubmit({ name: name.trim(), charges });
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isSubmitting} fullWidth maxWidth="sm">
      <DialogTitle>Créer une zone de livraison</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={NAME_INPUT_ID}
            label="Nom"
            inputProps={{ maxLength: DELIVERY_ZONE_NAME_MAX_LENGTH }}
            error={name !== '' && isNameInvalid}
            helperText={name !== '' && isNameInvalid ? DELIVERY_ZONE_NAME_ERROR : undefined}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {charges.map((charge, index) => (
            <div key={index} className={classes.chargeRow}>
              <TextField
                size="small"
                variant="outlined"
                type="number"
                id={`create-delivery-zone-threshold-${index}`}
                label={`Palier ${index + 1} : seuil (€)`}
                inputProps={{ min: 0, step: 0.01 }}
                value={charge.threshold}
                onChange={(event) => handleChargeChange(index, 'threshold', event.target.value)}
              />
              <TextField
                size="small"
                variant="outlined"
                type="number"
                id={`create-delivery-zone-costs-ht-${index}`}
                label="Frais HT (€)"
                inputProps={{ min: 0, step: 0.01 }}
                value={charge.costsHT}
                onChange={(event) => handleChargeChange(index, 'costsHT', event.target.value)}
              />
              <TextField
                size="small"
                variant="outlined"
                id={`create-delivery-zone-costs-ttc-${index}`}
                label="Frais TTC"
                value={formatPrice(computeCostsTTC(charge.costsHT))}
                InputProps={{ readOnly: true }}
              />
            </div>
          ))}
          {areChargesInvalid && <FormHelperText error>{CHARGES_ERROR}</FormHelperText>}
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
